import { CONTACT_EMAIL } from "../config";
import { DemoForm } from "../components/DemoForm";

// The DrStack landing page — single page, sections in the original site's order:
// hero → trust strip → platform (6 modules) → how it works (3 steps) → AI band → stats →
// security/compliance → demo CTA. All copy is verbatim from the scraped site (docs: copy.md).

function Hero() {
  return (
    <header className="hero" id="top">
      <div className="wrap hero-grid">
        <div>
          <span className="eyebrow">
            <span className="dot" />
            AI-POWERED TELEHEALTH INFRASTRUCTURE
          </span>
          <h1>
            Launch telehealth in weeks, <span className="accent">not years.</span>
          </h1>
          <p className="lede">
            DrStack is the end-to-end, turnkey platform that lets any organization offer virtual care under its own
            brand — scheduling, video visits, e-prescribing, billing, and an AI clinical copilot, all in one stack.
          </p>
          <div className="hero-ctas">
            <a className="btn btn-primary" href="#contact">
              Book a Demo
            </a>
            <a className="btn btn-ghost" href="#platform">
              Explore the Platform
            </a>
          </div>
          <p className="hero-note">HIPAA-compliant · SOC 2 Type II · White-label ready</p>
        </div>
        <div className="mock" aria-hidden="true">
          <div className="mock-bar">
            <i />
            <i />
            <i />
          </div>
          <div className="mock-body">
            <div className="mock-card full">
              <div className="mock-avatar">JM</div>
              <div style={{ flex: 1 }}>
                <div className="mock-line w40" style={{ background: "#CFE8F6" }} />
                <div className="mock-line w80" />
              </div>
              <svg className="ecg" style={{ width: 120 }} viewBox="0 0 120 44">
                <path
                  d="M0 22 H30 L38 8 L48 38 L56 22 H120"
                  fill="none"
                  stroke="#14B8A6"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="mock-card">
              <span className="mock-tag blue">AI TRIAGE</span>
              <div className="mock-line w80" />
              <div className="mock-line w60" />
              <div className="mock-line w40" />
            </div>
            <div className="mock-card">
              <span className="mock-tag">VISIT NOTES · AUTO</span>
              <div className="mock-line w60" />
              <div className="mock-line w80" />
              <div className="mock-line w60" />
            </div>
            <div className="mock-card full" style={{ background: "#fff" }}>
              <div style={{ flex: 1 }}>
                <span className="mock-tag blue">TODAY</span>
                <div className="mock-line w80" />
              </div>
              <div style={{ flex: 1 }}>
                <span className="mock-tag">RX SENT</span>
                <div className="mock-line w60" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

function TrustStrip() {
  return (
    <div className="strip">
      <div className="wrap strip-inner">
        <span>TRUSTED BY FORWARD-THINKING ORGANIZATIONS</span>
        <span>HEALTH SYSTEMS</span>
        <span>EMPLOYERS</span>
        <span>PAYERS</span>
        <span>DIGITAL HEALTH BRANDS</span>
        <span>PHARMACIES</span>
      </div>
    </div>
  );
}

// Six platform modules — 24×24 stroke-only icons (stroke-width 2) alternating sky/teal.
const MODULES: { icon: JSX.Element; title: string; body: string }[] = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M8 21h8M12 18v3" />
      </svg>
    ),
    title: "Branded Patient Experience",
    body: "White-label web and mobile apps under your name. Intake, scheduling, reminders, and secure messaging — designed for conversion and retention.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2" strokeLinecap="round">
        <path d="M23 7l-7 5 7 5V7z" />
        <rect x="1" y="5" width="15" height="14" rx="2" />
      </svg>
    ),
    title: "HD Video Visits",
    body: "HIPAA-compliant video, audio, and async chat visits with waiting rooms, group sessions, and interpreter support built in.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round">
        <path d="M12 2v20M2 12h20" />
        <circle cx="12" cy="12" r="9" />
      </svg>
    ),
    title: "Clinical Network & Credentialing",
    body: "Bring your own providers or tap our 50-state clinician network. Licensing, credentialing, and malpractice coverage handled.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2" strokeLinecap="round">
        <path d="M9 12h6M9 16h6M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
        <path d="M13 2v7h7" />
      </svg>
    ),
    title: "e-Prescribing & Labs",
    body: "EPCS-certified e-prescribing, pharmacy routing, and nationwide lab ordering with results flowing straight back into the chart.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
      </svg>
    ),
    title: "Billing & Payments",
    body: "Insurance eligibility checks, claims submission, cash-pay checkout, and subscription plans — revenue infrastructure included.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2" strokeLinecap="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <path d="M3.3 7L12 12l8.7-5M12 22V12" />
      </svg>
    ),
    title: "EHR & API Integrations",
    body: "FHIR-native APIs and prebuilt connectors for Epic, Cerner, and Athena keep DrStack in sync with the systems you already run.",
  },
];

function Platform() {
  return (
    <section id="platform">
      <div className="wrap center">
        <div className="kicker">The Platform</div>
        <h2>Everything you need to deliver virtual care</h2>
        <p className="sub">
          One integrated stack replaces a dozen vendors. Launch a complete, branded telehealth service without
          building anything from scratch.
        </p>
        <div className="grid3" style={{ textAlign: "left" }}>
          {MODULES.map((m) => (
            <div className="card" key={m.title}>
              <div className="icon">{m.icon}</div>
              <h3>{m.title}</h3>
              <p>{m.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  {
    title: "Configure",
    body: "Pick your care lines — urgent care, behavioral health, chronic care, weight management, and more. Apply your brand, pricing, and clinical protocols.",
  },
  {
    title: "Connect",
    body: "We integrate with your EHR, eligibility systems, and pharmacy partners, and stand up your clinician network with full credentialing.",
  },
  {
    title: "Launch & Scale",
    body: "Go live under your brand. Our AI operations layer optimizes scheduling, engagement, and outcomes as your volume grows.",
  },
];

function HowItWorks() {
  return (
    <section className="how" id="how">
      <div className="wrap center">
        <div className="kicker">How It Works</div>
        <h2>From contract to live care in three steps</h2>
        <p className="sub">
          Turnkey means turnkey. Most partners launch a fully branded telehealth service in under 6 weeks.
        </p>
        <div className="steps" style={{ textAlign: "left" }}>
          {STEPS.map((s, i) => (
            <div className="step" key={s.title}>
              <div className="step-num">{i + 1}</div>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const AI_CARDS = [
  {
    name: "Smart Triage",
    when: "before the visit",
    body: "Conversational intake assesses symptoms, flags urgency, and routes patients to the right care line and clinician — cutting misrouted visits and wait times.",
  },
  {
    name: "Clinical Copilot",
    when: "during the visit",
    body: "Ambient documentation drafts SOAP notes in real time, surfaces relevant history, and suggests evidence-based order sets for clinician review.",
  },
  {
    name: "Automated Follow-up",
    when: "after the visit",
    body: "Personalized care plans, medication adherence nudges, and escalation alerts keep patients on track between visits without added staff burden.",
  },
  {
    name: "Operations Intelligence",
    when: "across your business",
    body: "Demand forecasting, provider capacity planning, and outcome analytics give leadership a live view of clinical and financial performance.",
  },
];

function AiBand() {
  return (
    <section className="ai-band" id="ai">
      <div className="wrap">
        <div className="kicker">Intelligence Layer</div>
        <h2>AI built into every step of care</h2>
        <p className="sub">
          DrStack isn&rsquo;t a video tool with AI bolted on. Machine intelligence runs through the entire platform —
          safely, with clinicians always in control.
        </p>
        <div className="ai-grid">
          {AI_CARDS.map((c) => (
            <div className="ai-card" key={c.name}>
              <h3>
                <em>{c.name}</em> — {c.when}
              </h3>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const STATS = [
  { n: "6 wks", l: "Average time to launch" },
  { n: "70%", l: "Lower cost vs. building in-house" },
  { n: "50", l: "States covered by clinician network" },
  { n: "4.9★", l: "Average patient visit rating" },
];

function Stats() {
  return (
    <section>
      <div className="wrap center">
        <div className="kicker">Why DrStack</div>
        <h2>The business case is simple</h2>
        <div className="stats">
          {STATS.map((s) => (
            <div className="stat" key={s.l}>
              <div className="n">{s.n}</div>
              <div className="l">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Trust() {
  return (
    <section id="trust" className="trust">
      <div className="wrap trust-grid">
        <div>
          <div className="kicker">Security &amp; Compliance</div>
          <h2>Enterprise-grade trust, built in from day one</h2>
          <p className="sub">
            Healthcare data demands more. DrStack is engineered for compliance at every layer, so your legal and
            security teams can say yes quickly.
          </p>
          <div className="badges">
            <span className="badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2.2" strokeLinecap="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              HIPAA Compliant
            </span>
            <span className="badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2.2" strokeLinecap="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              SOC 2 Type II
            </span>
            <span className="badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2.2" strokeLinecap="round">
                <rect x="3" y="11" width="18" height="10" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              End-to-End Encryption
            </span>
            <span className="badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2.2" strokeLinecap="round">
                <path d="M12 2v20M2 12h20" />
              </svg>
              HITRUST-Aligned
            </span>
          </div>
        </div>
        <div className="card trust-card">
          <h3>Responsible AI in medicine</h3>
          <p>
            Every AI output on DrStack is advisory. Clinicians review and approve all notes, orders, and prescriptions
            before anything reaches a patient record.
          </p>
          <p>
            Models are audited for accuracy and bias, PHI never trains shared models, and every AI interaction is
            logged for full traceability.
          </p>
          <p>Your data stays yours — isolated, encrypted, and exportable at any time.</p>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact">
      <div className="wrap">
        <div className="cta">
          <div>
            <h2>Ready to offer telehealth under your brand?</h2>
            <p>
              See the full platform in a 30-minute walkthrough. We&rsquo;ll map your use case to a launch plan — no
              commitment required.
            </p>
            <p className="direct">
              Prefer email? Reach us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>
          </div>
          <DemoForm />
        </div>
      </div>
    </section>
  );
}

export function Home() {
  return (
    <>
      <Hero />
      <TrustStrip />
      <Platform />
      <HowItWorks />
      <AiBand />
      <Stats />
      <Trust />
      <Contact />
    </>
  );
}
